import { useState, useEffect } from 'react';
import { partnerApi } from '../services/api/partnerApi';
import { useAuth } from './useAuth';

/**
 * Partner-portal access check.
 *
 * Access is granted when EITHER of these is true:
 *  - A valid Partner API key is in localStorage (legacy / external use)
 *  - The current Supabase session belongs to a PARTNER-role tuser
 *
 * For non-PARTNER roles (ADMIN/CUSTOMER/GUEST) with no API key we
 * short-circuit to false instead of probing `/api/partner/session`,
 * because that probe would intentionally 403 for those users and was
 * generating spurious "Partner role required" errors on every page that
 * mounts the navbar (e.g. the admin panel).
 */
export function usePartnerAuth() {
  const { role, loading: authLoading } = useAuth();
  const [isPartner, setIsPartner] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [authMode, setAuthMode] = useState<'session' | 'apiKey' | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (authLoading) {
      setIsLoading(true);
      return;
    }

    const apiKey = partnerApi.getPartnerApiKey();

    // Skip the probe entirely if the user can't possibly be a partner.
    if (!apiKey && role !== 'PARTNER') {
      setIsPartner(false);
      setPartnerId(null);
      setAuthMode(null);
      setIsLoading(false);
      return;
    }

    const check = async () => {
      try {
        const result = await partnerApi.checkSession();
        if (cancelled) return;
        setIsPartner(true);
        setPartnerId(result.partnerId);
        setAuthMode(result.authMode);
      } catch (error) {
        if (cancelled) return;
        // Probe failed -> not a partner. If the failure was due to a stale API
        // key, drop it so the next session login can work without juggling.
        if (apiKey) partnerApi.clearPartnerApiKey();
        setIsPartner(false);
        setPartnerId(null);
        setAuthMode(null);
        console.warn('Partner session check failed:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [role, authLoading]);

  return {
    isPartner,
    isLoading,
    partnerId,
    authMode,
    /** @deprecated kept for backward compat - use partnerId instead */
    apiKey: partnerApi.getPartnerApiKey(),
  };
}
